import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const Shifts = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title2">Shifts and staff work</Typography>
      <Typography>
        AmbuNet separates assigned work from open opportunities. Managers can
        assign shifts directly, publish rosters for acknowledgement, and
        advertise suitable event or patient transport shifts for staff to apply
        for.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Staff self-service</Typography>
      <Typography>
        Staff-facing shift tools sit under My Work. Available Shifts shows open
        work they can apply for, while <DocLink url="/my-shifts" text="My Shifts" />
        shows assigned upcoming work and roster shifts that need attention.
      </Typography>
      <Typography>
        Staff can also use <DocLink url="/my-availability" text="My Availability" />
        to submit leave, sickness, and unavailable periods so planners can avoid
        assigning them when they are not available.
      </Typography>
      <Typography>
        Looking for your own shifts? See{' '}
        <DocLink url="/crew/your-shifts" text="Your Shifts" docsPage />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Acknowledgements and declines</Typography>
      <Typography>
        Publishing a roster marks assigned roster shifts as requiring
        acknowledgement and notifies staff. The same applies when a manager
        assigns someone directly to an event or PTS shift — any shift a staff
        member is given (rather than one they applied for) asks for their
        acknowledgement. Staff can acknowledge that they have seen the shift
        or mark that they cannot attend, and My Shifts groups shifts needing
        acknowledgement together.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Declining a published assigned shift does not remove the staff member
        from the shift. It flags the issue for managers while preserving the
        original assignment for audit.
      </Alert>
      <Typography>
        If a staff member can no longer work a confirmed shift, they can
        instead offer it to colleagues as a swap — see{' '}
        <DocLink url="/employees-hr/shift-swaps" text="Shift Swaps" docsPage />
        .
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Open shifts and applications</Typography>
      <Typography>
        Event and PTS shifts can be advertised through{' '}
        <DocLink url="/available-shifts" text="Available Shifts" />. Staff can
        apply for available work, and managers can review applications using the
        normal shift application process.
      </Typography>
      <Typography>
        The board can be filtered by shift type — Events, PTS, or Rota — and
        by clinical role, on both the website and the app. Each filter shows
        how many shifts it contains, and the filtering is applied by AmbuNet
        itself so the counts and the list always agree. Turning on Show My
        Downtime includes events the staff member has marked themselves
        unavailable for.
      </Typography>
      <Typography>
        Opening a PTS shift shows the duty it belongs to, the vehicle allocated
        to that duty (or "No vehicle assigned" if none has been yet), and who
        the work is for: the organisation for a dedicated duty or for
        organisation-funded journeys, otherwise "Patient is the customer". No
        patient details are shown before the shift is assigned.
      </Typography>
      <Typography>
        When a manager advertises several people for the same role, date, and
        time — four Paramedics on an event day, for example — staff see one
        listing rather than a separate row for each vacancy. The listing shows
        how many shifts are still available and how many have already been
        filled. Anyone can apply to the listing; AmbuNet spreads applicants
        across the underlying vacancies automatically, respecting any
        per-shift application limit, so staff never need to work out which
        specific slot to request.
      </Typography>
      <Typography>
        Managers review every applicant for a vacancy together from{' '}
        <DocLink url="/shift-applications" text="Shift Applications" />,
        selecting up to the number of remaining slots to accept in one action.
        A separate Reject Remaining Applicants action then declines everyone
        else and sends the usual rejection notification, instead of reviewing
        applicants one at a time.
      </Typography>
      <Typography>
        Global Roster includes pending shift applications for the selected date
        range. Identical vacant shifts on the same day collapse into a single
        card with a count badge, and a small icon marks any vacant shift with
        pending applications. Managers can review applications directly from
        the roster, and — for event, PTS, and frontline shifts — edit the
        shift's time, role, pay, notes, and bookability right from the roster
        drawer, without needing to open the source module. Editing a
        collapsed card updates every identical shift it represents at once, so
        the drawer warns how many that is and disables assignment — assigning
        one specific person, or editing just one shift, still has to happen
        from the source module.
      </Typography>
      <Typography>
        Draft rota-plan shifts are highlighted in teal on Global Roster. When a
        draft rota plan is selected, only that plan's draft shifts are shown;
        draft shifts from other plans are hidden so the planner can focus on the
        current plan.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Subscribe to your shift calendar</Typography>
      <Typography>
        Staff can see their assigned shifts in their own calendar app. The{' '}
        <strong>Calendar</strong> tab of{' '}
        <DocLink url="/my-account" text="My Account" /> issues personal iCal
        feed links that Google Calendar, Outlook, and Apple Calendar can
        subscribe to, so upcoming shifts appear alongside everything else and
        update automatically. Create a separate link per device or app — each
        link can be copied, reset, or revoked on its own, so a lost phone only
        means revoking that one feed.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Source modules</Typography>
      <Typography>
        Event and PTS shifts shown in Global Roster remain linked to their
        source module. Use source buttons such as Open Event or Open PTS Duty to
        edit those shifts in the place they were created. If a draft rota plan
        is selected, the shift drawer also lets managers copy the shift into the
        current rota plan as a new pre-filled rota-plan shift.
      </Typography>
    </Stack>
  </Stack>
)

export default Shifts
